import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Camera, MapPin, Zap, CheckCircle, Clock, ThumbsUp, 
  AlertTriangle, Droplets, Crosshair, FileText, Construction, 
  Lightbulb, Brain, Sparkles // Added Brain and Sparkles
} from 'lucide-react';

// WEB3 Imports
import { useWeb3 } from '../context/Web3Context';
import { ethers } from 'ethers';

// FIREBASE Imports
import { db, storage } from '../firebase'; 
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'; 
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// --- AI INTEL BOT COMPONENT ---
const AIIntelBot = ({ hazardType }) => {
  // Simple predictive logic based on hazard type
  const intelData = {
    'Pothole': { confidence: "85%", reward: "Low-Med", trend: "Increasing" },
    'Water Logging': { confidence: "72%", reward: "Medium", trend: "Stable" },
    'Accident': { confidence: "98%", reward: "High", trend: "Urgent" },
    'Broken Light': { confidence: "60%", reward: "Low", trend: "Decreasing" },
    'Construction': { confidence: "92%", reward: "Low", trend: "Constant" },
    'Slippery': { confidence: "78%", reward: "Medium", trend: "Localized" }
  };

  const current = intelData[hazardType] || intelData['Pothole'];

  return (
    <div className="ai-intel-container glass-panel">
      <div className="ai-header">
        <div className="glowing-bot">
          <Brain size={24} className="ai-icon-main" />
          <div className="glow-effect"></div>
        </div>
        <div className="ai-status">
          <h4>Urban Intel AI</h4>
          <span className="ai-live-tag">● Processing Live Data</span>
        </div>
        <Sparkles size={16} className="ai-sparkle" />
      </div>

      <div className="ai-content">
        <div className="ai-stat">
          <label>Verification Confidence</label>
          <div className="val green">{current.confidence}</div>
        </div>
        <div className="ai-stat">
          <label>Est. Reward Tier</label>
          <div className="val blue">{current.reward}</div>
        </div>
        <div className="ai-stat">
          <label>Priority Trend</label>
          <div className="val gold">{current.trend}</div>
        </div>
      </div>
      
      <p className="ai-prediction">
        "Our neural network suggests staking on <strong>{hazardType}</strong> in this sector due to high verification frequency."
      </p>

      <style>{`
        .ai-intel-container {
          margin-top: 20px;
          border: 1px solid rgba(139, 92, 246, 0.3);
          background: rgba(139, 92, 246, 0.05);
          padding: 20px;
          position: relative;
          overflow: hidden;
        }
        .ai-header { display: flex; align-items: center; gap: 15px; margin-bottom: 15px; }
        .glowing-bot { position: relative; display: flex; align-items: center; justify-content: center; width: 40px; height: 40px; }
        .ai-icon-main { color: #8b5cf6; z-index: 2; animation: float 3s ease-in-out infinite; }
        .glow-effect {
          position: absolute;
          width: 30px;
          height: 30px;
          background: #8b5cf6;
          border-radius: 50%;
          filter: blur(15px);
          opacity: 0.6;
          animation: pulse-glow 2s infinite;
        }
        .ai-live-tag { font-size: 0.7rem; color: #10b981; text-transform: uppercase; font-weight: 700; letter-spacing: 0.5px; }
        .ai-status h4 { margin: 0; font-size: 1rem; color: #f1f5f9; }
        .ai-sparkle { color: #8b5cf6; margin-left: auto; opacity: 0.6; }
        
        .ai-content { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 15px; }
        .ai-stat label { display: block; font-size: 0.65rem; color: #94a3b8; margin-bottom: 4px; }
        .ai-stat .val { font-size: 0.9rem; font-weight: 800; }
        .val.green { color: #10b981; }
        .val.blue { color: #3b82f6; }
        .val.gold { color: #f59e0b; }
        
        .ai-prediction { font-size: 0.75rem; color: #94a3b8; font-style: italic; border-top: 1px solid rgba(255,255,255,0.05); padding-top: 10px; margin: 0; }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes pulse-glow {
          0% { transform: scale(0.8); opacity: 0.4; }
          50% { transform: scale(1.2); opacity: 0.8; }
          100% { transform: scale(0.8); opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

const Report = () => {
  const navigate = useNavigate();
  const locationState = useLocation().state || {};
  
  const web3Context = useWeb3();
  const { account, provider, switchToMonad } = web3Context || {}; 

  const initialAddress = locationState.selectedAddress || '';
  
  const [formData, setFormData] = useState({
    hazardType: 'Pothole',
    description: '',
    location: initialAddress,
    lat: 26.9124, 
    lng: 75.7873
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  const [reports, setReports] = useState([
    { id: 1, type: 'Accident', location: 'MI Road, Jaipur', time: '10m ago', votes: 42, user: 'Rahul_S', status: 'Critical', color: '#ef4444', icon: <AlertTriangle size={18} color="#ef4444" /> },
    { id: 2, type: 'Water Logging', location: 'Tonk Road', time: '25m ago', votes: 18, user: 'Priya_22', status: 'Active', color: '#3b82f6', icon: <Droplets size={18} color="#3b82f6" /> },
    { id: 3, type: 'Broken Light', location: 'C-Scheme', time: '1h ago', votes: 8, user: 'Vikram_X', status: 'Pending', color: '#fbbf24', icon: <Lightbulb size={18} color="#fbbf24" /> },
  ]);

  const hazardOptions = [
    { id: 'Pothole', icon: '🕳️', label: 'Pothole' },
    { id: 'Water Logging', icon: '🌊', label: 'Water Logging' },
    { id: 'Accident', icon: '💥', label: 'Accident' },
    { id: 'Broken Light', icon: '💡', label: 'Broken Light' },
    { id: 'Construction', icon: '🚧', label: 'Construction' },
    { id: 'Slippery', icon: '❄️', label: 'Slippery' }
  ];
  
  const handleLiveLocation = () => {
    if (!navigator.geolocation) return alert("Geolocation not supported");
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setFormData(prev => ({ 
          ...prev, 
          location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)} (Live)`,
          lat: latitude,
          lng: longitude
        }));
        setIsLocating(false);
      },
      () => { alert("Unable to retrieve location"); setIsLocating(false); }
    );
  };

  const handleImageUpload = (e) => {
    if (e.target.files[0]) setImageFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!account) return alert("Please connect your Wallet first!");

    setIsSubmitting(true);
    
    try {
      setStatusMsg("Checking Wallet Network...");
      const network = await provider.getNetwork();
      if (network.chainId.toString() !== '10143') { 
         setStatusMsg("Switching to Monad Testnet...");
         try {
            await switchToMonad();
         } catch (err) {
            console.warn("Network switch failed or rejected.", err);
         }
      }

      const signer = await provider.getSigner();
      const HARDCODED_ADDRESS = "0x03Ee3Bd53aF5711c5e3e8346D4b2e2E3e31394be";
      const NATIVE_ABI = ["function reportHazard(string memory _hazardType, int256 _lat, int256 _lng) external payable"];
      const hazardContract = new ethers.Contract(HARDCODED_ADDRESS, NATIVE_ABI, signer);
      
      const latInt = Math.floor(formData.lat * 1000000);
      const lngInt = Math.floor(formData.lng * 1000000);
      const stakeInMon = ethers.parseEther("0.0001").toString(); 

      setStatusMsg("Confirming Stake on Wallet...");
      const tx = await hazardContract.reportHazard(formData.hazardType, latInt, lngInt, { value: stakeInMon });
      
      setStatusMsg("Waiting for Blockchain Confirmation...");
      await tx.wait();
      const txHash = tx.hash;

      let imageUrl = "";
      if (imageFile) {
        setStatusMsg("Uploading Evidence...");
        try {
          const imageRef = ref(storage, `evidence/${Date.now()}_${imageFile.name}`);
          const snapshot = await uploadBytes(imageRef, imageFile);
          imageUrl = await getDownloadURL(snapshot.ref);
        } catch (err) {
          console.warn("Image upload failed, continuing with text only.");
        }
      }

      setStatusMsg("Saving Report...");
      const newReport = {
        type: formData.hazardType,
        description: formData.description,
        location: formData.location,
        coordinates: { lat: formData.lat, lng: formData.lng },
        reporter: account,
        imageUrl: imageUrl,
        txHash: txHash,
        timestamp: serverTimestamp(),
        votes: 0,
        status: "Pending Verification"
      };
      
      await addDoc(collection(db, "reports"), newReport);

      const visualReport = {
        id: Date.now(),
        type: formData.hazardType,
        location: formData.location,
        time: 'Just now',
        votes: 0,
        user: 'You',
        status: 'Pending',
        color: '#8b5cf6',
        icon: <Zap size={18} color="#8b5cf6" />
      };
      setReports([visualReport, ...reports]);
      setIsSuccess(true);

    } catch (error) {
      console.error("Submission Error:", error);
      alert("Transaction Failed: " + (error.reason || error.message));
    }
    
    setIsSubmitting(false);
    setStatusMsg("");
  };

  if (isSuccess) {
    return (
      <div className="report-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
        <div className="glass-panel" style={{ textAlign: 'center', padding: '50px', maxWidth: '500px' }}>
          <CheckCircle size={80} color="#10b981" style={{ margin: '0 auto 20px auto' }} />
          <h2 style={{ fontSize: '2rem', marginBottom: '10px' }}>Report Submitted!</h2>
          <p style={{ color: '#94a3b8', marginBottom: '30px' }}>Your stake has been recorded on the Citizen Network.</p>
          <div style={{ display: 'flex', gap: '15px' }}>
            <button 
              onClick={() => navigate('/map', { 
                state: { 
                  focusLat: formData.lat, 
                  focusLng: formData.lng,
                  focusZoom: 18 
                } 
              })} 
              className="btn-primary" 
              style={{ flex: 1 }}
            >
              View on Map
            </button>
            <button onClick={() => setIsSuccess(false)} className="btn-secondary" style={{ flex: 1 }}>Report Another</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-wrapper">
      
      <div className="header-section">
        <h1>Citizen Intelligence Network</h1>
        <p>Stake on new hazards or verify existing ones to earn reputation.</p>
      </div>

      <div className="main-grid">
        
        {/* LEFT COLUMN: THE FORM */}
        <div className="glass-panel form-panel">
          
          <div className="panel-header">
            <div className="icon-box warning-bg">
              <Zap size={24} color="#ef4444" fill="#ef4444" />
            </div>
            <div>
              <h2>New Wager</h2>
              <span>Stake 0.0001 MON to open a report</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            
            <div className="form-group">
              <label>Hazard Type</label>
              <div className="hazard-grid">
                {hazardOptions.map(opt => (
                  <div 
                    key={opt.id} 
                    className={`hazard-card ${formData.hazardType === opt.id ? 'active' : ''}`}
                    onClick={() => setFormData({...formData, hazardType: opt.id})}
                  >
                    <span className="emoji">{opt.icon}</span>
                    <span className="label">{opt.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Location</label>
              <div className="location-actions">
                <button type="button" onClick={handleLiveLocation} className="btn-glass small">
                  {isLocating ? 'Locating...' : <><Crosshair size={16} /> Live Location</>}
                </button>
                <button type="button" onClick={() => navigate('/map', { state: { selectMode: true } })} className="btn-glass small">
                  <MapPin size={16} /> Pick on Map
                </button>
              </div>
              <input 
                type="text" 
                className="input-glass" 
                placeholder="Enter address or coordinates..." 
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>Details</label>
              <textarea 
                className="input-glass" 
                rows="3" 
                placeholder="Describe the hazard..." 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              ></textarea>
            </div>

            <div className="upload-zone">
              <input type="file" onChange={handleImageUpload} accept="image/*" />
              <Camera size={24} color={imageFile ? "#10b981" : "#64748b"} />
              <span>{imageFile ? imageFile.name : "Click to Upload Evidence"}</span>
            </div>

            {/* AI INTEL BOT PLACEMENT */}
            <AIIntelBot hazardType={formData.hazardType} />

            <button type="submit" className="btn-primary" disabled={isSubmitting} style={{ marginTop: '20px' }}>
              {isSubmitting ? statusMsg || "Stake & Report" : "Stake & Report"}
            </button>

          </form>
        </div>

        {/* RIGHT COLUMN: EXISTING REPORTS */}
        <div className="glass-panel list-panel">
          <div className="panel-header" style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <FileText size={24} color="#3b82f6" />
              <div>
                <h2>Existing Reports</h2>
                <span>Verify reports to improve accuracy</span>
              </div>
            </div>
            <div className="live-badge">● Live</div>
          </div>

          <div className="reports-list">
            {reports.map((report) => (
              <div key={report.id} className="report-item">
                <div className="report-top">
                  <div className="report-type">
                    {report.icon}
                    <span>{report.type}</span>
                  </div>
                  <div className="report-user">
                    by {report.user}
                  </div>
                </div>
                
                <div className="report-loc">
                  <MapPin size={12} /> {report.location}
                </div>

                <div className="report-bottom">
                  <div className="report-meta">
                    <span className="time"><Clock size={12}/> {report.time}</span>
                    <span className="status" style={{ color: report.color }}>● {report.status}</span>
                  </div>
                  <button className="btn-vote">
                    <ThumbsUp size={14} /> Upvote ({report.votes})
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      <style>{`
        :root {
          --glass-bg: rgba(30, 41, 59, 0.7);
          --glass-border: rgba(255, 255, 255, 0.08);
          --primary-blue: #3b82f6;
          --primary-glow: rgba(59, 130, 246, 0.5);
          --text-main: #f1f5f9;
          --text-muted: #94a3b8;
        }
        .report-wrapper { padding: 100px 20px 40px; max-width: 1200px; margin: 0 auto; color: var(--text-main); font-family: 'Inter', sans-serif; }
        .header-section { text-align: center; margin-bottom: 40px; }
        .header-section h1 { font-size: 2.5rem; margin-bottom: 10px; font-weight: 700; background: linear-gradient(to right, #fff, #94a3b8); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .header-section p { color: var(--text-muted); }
        .main-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(400px, 1fr)); gap: 30px; alignItems: start; }
        .glass-panel { background: var(--glass-bg); backdrop-filter: blur(12px); border: 1px solid var(--glass-border); border-radius: 20px; padding: 30px; box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3); }
        .panel-header { display: flex; alignItems: center; gap: 15px; margin-bottom: 25px; padding-bottom: 20px; border-bottom: 1px solid var(--glass-border); }
        .panel-header h2 { font-size: 1.25rem; margin: 0; font-weight: 600; }
        .panel-header span { font-size: 0.85rem; color: var(--text-muted); }
        .icon-box { padding: 10px; border-radius: 12px; background: rgba(239, 68, 68, 0.1); }
        .form-group { margin-bottom: 20px; }
        .form-group label { display: block; margin-bottom: 8px; font-size: 0.9rem; color: var(--text-muted); font-weight: 500; }
        .hazard-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        .hazard-card { background: rgba(255,255,255,0.03); border: 1px solid var(--glass-border); border-radius: 12px; padding: 15px; text-align: center; cursor: pointer; transition: all 0.2s; }
        .hazard-card:hover { background: rgba(255,255,255,0.06); }
        .hazard-card.active { border-color: var(--primary-blue); background: rgba(59, 130, 246, 0.1); box-shadow: 0 0 15px var(--primary-glow); }
        .hazard-card .emoji { font-size: 1.5rem; display: block; margin-bottom: 5px; }
        .hazard-card .label { font-size: 0.75rem; color: var(--text-muted); }
        .location-actions { display: flex; gap: 10px; margin-bottom: 10px; }
        .input-glass { width: 100%; background: rgba(0, 0, 0, 0.2); border: 1px solid var(--glass-border); padding: 12px; border-radius: 8px; color: white; font-size: 0.95rem; outline: none; transition: border-color 0.2s; }
        .input-glass:focus { border-color: var(--primary-blue); }
        .upload-zone { border: 2px dashed var(--glass-border); border-radius: 12px; padding: 20px; text-align: center; position: relative; cursor: pointer; transition: border-color 0.2s; display: flex; flex-direction: column; alignItems: center; gap: 8px; }
        .upload-zone:hover { border-color: var(--primary-blue); background: rgba(59, 130, 246, 0.05); }
        .upload-zone input { position: absolute; top:0; left:0; width:100%; height:100%; opacity:0; cursor: pointer; }
        .upload-zone span { font-size: 0.85rem; color: var(--text-muted); }
        .btn-primary { width: 100%; padding: 14px; background: linear-gradient(135deg, #3b82f6, #6366f1); border: none; border-radius: 10px; color: white; font-weight: 600; cursor: pointer; font-size: 1rem; margin-top: 10px; transition: transform 0.2s; }
        .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 4px 15px var(--primary-glow); }
        .btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }
        .btn-glass { background: rgba(255,255,255,0.05); border: 1px solid var(--glass-border); color: var(--text-muted); padding: 8px 12px; border-radius: 8px; cursor: pointer; display: flex; alignItems: center; justifyContent: center; gap: 6px; font-size: 0.85rem; flex: 1; }
        .btn-glass:hover { background: rgba(255,255,255,0.1); color: white; }
        .list-panel { height: 100%; }
        .live-badge { background: rgba(16, 185, 129, 0.1); color: #10b981; border: 1px solid rgba(16, 185, 129, 0.2); padding: 4px 10px; border-radius: 20px; font-size: 0.75rem; font-weight: 700; animation: pulse 2s infinite; }
        .reports-list { display: flex; flex-direction: column; gap: 15px; }
        .report-item { background: rgba(255,255,255,0.02); border: 1px solid var(--glass-border); border-radius: 12px; padding: 15px; transition: transform 0.2s; }
        .report-item:hover { transform: translateY(-2px); background: rgba(255,255,255,0.04); }
        .report-top { display: flex; justifyContent: space-between; margin-bottom: 8px; }
        .report-type { display: flex; alignItems: center; gap: 8px; fontWeight: 600; font-size: 0.95rem; }
        .report-user { font-size: 0.8rem; color: #6366f1; }
        .report-loc { display: flex; alignItems: center; gap: 5px; color: var(--text-muted); font-size: 0.85rem; margin-bottom: 12px; }
        .report-bottom { display: flex; justifyContent: space-between; alignItems: center; paddingTop: 10px; borderTop: 1px solid var(--glass-border); }
        .report-meta { display: flex; gap: 10px; font-size: 0.75rem; color: var(--text-muted); }
        .report-meta span { display: flex; alignItems: center; gap: 4px; }
        .btn-vote { background: transparent; border: 1px solid var(--glass-border); color: var(--text-muted); padding: 4px 10px; border-radius: 20px; font-size: 0.75rem; cursor: pointer; display: flex; alignItems: center; gap: 5px; }
        .btn-vote:hover { border-color: var(--primary-blue); color: var(--primary-blue); }
        @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }
        @media (max-width: 768px) { .main-grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
};

export default Report;